import { useEffect, useState } from 'react';
import {
  DATA_LOCATION_OPTIONS,
  FREQUENCY_OPTIONS,
  IMPACT_OPTIONS,
  PEOPLE_OPTIONS,
  SENSITIVITY_OPTIONS,
} from '../constants.js';
import { clearDraft, readDraft, saveDraft } from '../persistence.js';
import type {
  DataLocation,
  Depth,
  Frequency,
  Impact,
  Intake,
  PeopleInvolved,
  Sensitivity,
} from '../types.js';
import { DepthSelector, Field, Select } from './fields.js';

const DRAFT_NS = 'evaluate';

interface IntakeFormProps {
  onSubmit: (intake: Intake, depth: Depth) => void;
}

/** The draft carries the intake fields plus the chosen depth (kept out of Intake,
 *  which must match the agent schema exactly). */
type Draft = Intake & { depth?: Depth };

/** Intake fields from a draft, with `depth` stripped so it never leaks into Intake. */
function intakeFromDraft(draft: Draft | null): Partial<Intake> {
  if (!draft) return {};
  const rest: Partial<Draft> = { ...draft };
  delete rest.depth;
  return rest;
}

const EMPTY: Intake = {
  name: '',
  email: '',
  company: '',
  website_url: '',
  pain_description: '',
  impact: [],
  impact_detail: '',
  frequency: '',
  people_involved: '',
  data_location: [],
  data_location_other: '',
  sensitivity: '',
};

export function IntakeForm({ onSubmit }: IntakeFormProps): React.JSX.Element {
  const [form, setForm] = useState<Intake>(() => ({
    ...EMPTY,
    ...intakeFromDraft(readDraft<Draft>(DRAFT_NS)),
  }));
  const [depth, setDepth] = useState<Depth>(() => readDraft<Draft>(DRAFT_NS)?.depth ?? 'fast');
  const [touched, setTouched] = useState(false);
  const [showDataOther, setShowDataOther] = useState(() =>
    Boolean((readDraft<Draft>(DRAFT_NS)?.data_location_other ?? '').trim()),
  );

  // Keep an in-progress draft so an accidental refresh doesn't lose their typing.
  useEffect(() => {
    saveDraft({ ...form, depth }, DRAFT_NS);
  }, [form, depth]);

  function toggleDataOther(): void {
    const next = !showDataOther;
    setShowDataOther(next);
    if (!next) set('data_location_other', ''); // clear when unchecked
  }

  function set<K extends keyof Intake>(key: K, value: Intake[K]): void {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function toggle<T extends string>(key: 'impact' | 'data_location', value: T): void {
    setForm((f) => {
      const list = f[key] as string[];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...f, [key]: next };
    });
  }

  const missing = validate(form);

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setTouched(true);
    if (missing.length === 0) {
      clearDraft(DRAFT_NS); // handed off to the server; the assessment id takes over from here
      onSubmit(form, depth);
    }
  }

  return (
    <form className="tbo-form" onSubmit={handleSubmit} noValidate>
      <p className="tbo-lead">
        Tell us about a task or problem that's costing you time or money. We'll read your site,
        ask a few sharp follow-ups, then give you an honest AI-readiness read.
      </p>

      <fieldset>
        <legend>About you</legend>
        <Field label="Your name" required>
          <input value={form.name} onChange={(e) => set('name', e.target.value)} />
        </Field>
        <Field label="Work email" required>
          <input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
        </Field>
        <Field label="Company" required>
          <input value={form.company} onChange={(e) => set('company', e.target.value)} />
        </Field>
        <Field label="Website URL" required>
          <input
            type="url"
            placeholder="https://…"
            value={form.website_url}
            onChange={(e) => set('website_url', e.target.value)}
          />
        </Field>
      </fieldset>

      <fieldset>
        <legend>The problem</legend>
        <Field label="Describe the task or problem that's costing you time or money" required>
          <textarea
            rows={4}
            value={form.pain_description}
            onChange={(e) => set('pain_description', e.target.value)}
          />
        </Field>

        <Field label="What does this problem cost you today?" required>
          <div className="tbo-checks">
            {IMPACT_OPTIONS.map((opt) => (
              <label key={opt} className="tbo-check">
                <input
                  type="checkbox"
                  checked={form.impact.includes(opt)}
                  onChange={() => toggle<Impact>('impact', opt)}
                />
                {opt}
              </label>
            ))}
          </div>
          <input
            className="tbo-detail"
            placeholder="Optional: add any detail"
            value={form.impact_detail}
            onChange={(e) => set('impact_detail', e.target.value)}
          />
        </Field>

        <Field label="How often does this happen?" required>
          <Select
            value={form.frequency}
            options={FREQUENCY_OPTIONS}
            onChange={(v) => set('frequency', v as Frequency)}
          />
        </Field>

        <Field label="How many people spend time on this today?" required>
          <Select
            value={form.people_involved}
            options={PEOPLE_OPTIONS}
            onChange={(v) => set('people_involved', v as PeopleInvolved)}
          />
        </Field>

        <Field label="Where does the relevant information live?" required>
          <div className="tbo-checks">
            {DATA_LOCATION_OPTIONS.map((opt) => (
              <label key={opt} className="tbo-check">
                <input
                  type="checkbox"
                  checked={form.data_location.includes(opt)}
                  onChange={() => toggle<DataLocation>('data_location', opt)}
                />
                {opt}
              </label>
            ))}
            <label className="tbo-check">
              <input type="checkbox" checked={showDataOther} onChange={toggleDataOther} />
              Other
            </label>
          </div>
          {showDataOther && (
            <input
              className="tbo-detail"
              placeholder="Please specify where else the information lives"
              value={form.data_location_other ?? ''}
              onChange={(e) => set('data_location_other', e.target.value)}
            />
          )}
        </Field>

        <Field label="How sensitive is the data involved?" required>
          <Select
            value={form.sensitivity}
            options={SENSITIVITY_OPTIONS}
            onChange={(v) => set('sensitivity', v as Sensitivity)}
          />
        </Field>
      </fieldset>

      <DepthSelector depth={depth} onChange={setDepth} />

      {touched && missing.length > 0 && (
        <div className="tbo-error">Please complete: {missing.join(', ')}.</div>
      )}

      <button className="tbo-btn" type="submit">
        Analyse my problem →
      </button>
    </form>
  );
}

function validate(form: Intake): string[] {
  const missing: string[] = [];
  if (!form.name.trim()) missing.push('name');
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) missing.push('a valid email');
  if (!form.company.trim()) missing.push('company');
  if (!/^https?:\/\/.+/.test(form.website_url)) missing.push('website URL');
  if (!form.pain_description.trim()) missing.push('problem description');
  if (form.impact.length === 0) missing.push('at least one cost');
  if (!form.frequency) missing.push('frequency');
  if (!form.people_involved) missing.push('people involved');
  if (form.data_location.length === 0 && !(form.data_location_other ?? '').trim())
    missing.push('data location');
  if (!form.sensitivity) missing.push('data sensitivity');
  return missing;
}
